/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { pick } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Button } from "../../../../components/Button";
import Input from "../../../../components/Input";
import { Loader } from "../../../../components/Loader";
import Modal from "../../../../components/Modal";
import { PadActionButton } from "../../../../components/Pad/Pad";
import Steppers from "../../../../components/Stepper/Stepper";
import TextField from "../../../../components/TextField";
import apiClient from "../../../../services/api-client";
import { selectRemoteConfig } from "../../../../services/firebase/remote-config/remoteConfig.slice";
import { useAppDispatch } from "../../../../store/rootStore";
import { TestIds } from "../../../../testIds";
import { getEditPriceReason } from "../../../../util/request/getEditPriceReason";
import useToast from "../../../../util/useToast";
import { isInvalidEmail } from "../../../../util/validateEmail";
import {
  BasicDigitalFarmerAccount,
  DigitalFarmerAccount,
  selectAllFarmerAccounts,
  selectCurrentFarmerAccount,
  selectIsThisTfo,
} from "../../../azure-b2c/storage";
import CartSlice from "../../../cart/storage/cart.slice";
import { CartItem } from "../../../cart/storage/cart.types";
import { selectStoreById } from "../../../stores/storage/stores.slice";
import { createColdOffer } from "../../storage/api/createColdOffer";
import {
  OfferQuotePayload,
  Quote,
  QuoteProductInfo,
  SearchQuotesResponse,
} from "../../storage/types";
import { updateAccountEmailInAccountList } from "../../utils/updateAccountEmail";
import { ExpiresOnDatePicker } from "../QuoteOffer/components/ExpiresOnDateSelector";
import { FreightChargeInput } from "../QuoteOffer/components/FreightChargeInput";
import { MultiAccountSelector } from "../QuoteOffer/components/MultiAccountSelector";
import { RestrictedCheckbox } from "../QuoteOffer/components/RestrictedCheckbox";

export interface SendOfferModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  quote?: Quote;
  products: CartItem[];
  // If cold, send to quote/offer api, not /quote/{id}/offer
  cold?: boolean;
  multiple?: boolean;
}

export const SendOfferModal = ({
  isModalOpen = false,
  setIsModalOpen,
  quote,
  products,
  cold = false,
  multiple = false,
}: SendOfferModalProps) => {
  const _account = useSelector(selectCurrentFarmerAccount);
  const account = useMemo(() => {
    if (!_account) {
      return undefined;
    }
    return {
      ..._account,
      contact: _account?.contact
        ? {
            email: quote?.contact.email ?? _account.contact.email,
            name: _account.contact.name,
            phone: _account.contact.phone,
          }
        : null,
    } as DigitalFarmerAccount;
  }, [_account, quote]);

  if (account) {
    return (
      <SendOfferModalInnerComponent
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        quote={quote}
        products={products}
        cold={cold}
        multiple={multiple}
        account={account}
      />
    );
  }
};

const SendOfferModalInnerComponent = ({
  isModalOpen = false,
  setIsModalOpen,
  quote,
  products,
  cold = false,
  multiple = false,
  account,
}: SendOfferModalProps & {
  account: DigitalFarmerAccount;
}) => {
  const allAccountsDictionary = useSelector(selectAllFarmerAccounts)?.accounts ?? {};
  const farmerAccounts = useMemo(() => {
    const allAccounts = updateAccountEmailInAccountList(
      Object.values(allAccountsDictionary),
      account
    );
    return Object.values(allAccounts).sort((a, b) => a.accountName.localeCompare(b.accountName));
  }, [account.accountNumber]);
  const updatedAccount = farmerAccounts.find((a) => a.accountNumber === account.accountNumber);
  const [accountList, setAccountList] = useState<BasicDigitalFarmerAccount[]>(
    updatedAccount ? [updatedAccount] : []
  );

  useEffect(() => {
    // this effect adds the current account back into the accountList if it was removed by click the X button
    if (accountList.length === 0) {
      setAccountList(updatedAccount ? [updatedAccount] : []);
    }
  }, [accountList]);

  const isTfo = useSelector(selectIsThisTfo);
  const { quoteMaxFutureDays } = useSelector(selectRemoteConfig);

  const [note, setNote] = useState<string | null>(null);
  const [expiryDate, setExpiryDate] = React.useState<dayjs.Dayjs | undefined>(
    dayjs()
      .endOf("day")
      .add(quoteMaxFutureDays ?? 7, "days")
  );

  const [dateError, setDateError] = React.useState<string>();
  const [freightCharge, setFreightCharge] = React.useState<string>("0.00");
  const [freightChargePrice, setFreightChargePrice] = React.useState<number>(0);

  const [isLocked, setIsLocked] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);

  const navigate = useNavigate();
  const { notify } = useToast();
  const dispatch = useAppDispatch();

  async function tfoCreateColdOffer() {
    if (account && isTfo && expiryDate) {
      setError(false);
      setLoading(true);
      try {
        const quoteProduct = products.map((e) => {
          return {
            ...e,
            itemId: e.itemNumber,
            editedPriceExcGst: e.editedPriceExcGst,
          } as QuoteProductInfo;
        });

        const accountsNumbers = Array.from(emailMap.entries()).map(([accountNumber, email]) => ({
          accountNumber,
          email: email.email,
        }));

        const response = await createColdOffer({
          accounts: accountsNumbers,
          isLocked: isLocked,
          delivery: {
            freightChargeExcGst: freightChargePrice,
          },
          expiresOn: expiryDate.toDate(),
          note: note ?? "",
          products: [...quoteProduct],
        });

        if (response.status.toString().startsWith("2")) {
          setLoading(false);
          setError(false);
          notify("Quote sent");
          setIsModalOpen(false);
          dispatch(CartSlice.actions.clearCart({ accountNumber: account.accountNumber }));
        } else {
          setLoading(false);
          setError(true);
        }
      } catch {
        setLoading(false);
        setError(true);
      }
    }
  }

  const onSubmit = () => {
    // False Positive, cold should be sufficient
    if (cold) {
      tfoCreateColdOffer();
    } else {
      tfoRespondWithOffer();
    }
  };

  async function tfoRespondWithOffer(): Promise<void> {
    try {
      setLoading(true);
      if (!quote?.version || !account?.accountNumber) {
        return;
      }

      const accountsNumbers = Array.from(emailMap.entries()).map(([accountNumber, email]) => ({
        accountNumber,
        email: email.email,
      }));

      const payload: OfferQuotePayload = {
        version: quote?.version,
        accounts: accountsNumbers,
        products: products.map<QuoteProductInfo>((product: CartItem) => {
          const editedReason: Partial<CartItem> = getEditPriceReason<CartItem>(product);

          return {
            ...pick(product, [
              "productId",
              "description",
              "quantity",
              "basePriceExcGst",
              "editedPriceExcGst",
              "discountedPriceExcGst",
              "discountedPriceIncGst",
            ]),
            itemId: product.itemNumber,
            description: product.description ?? product.name,
            ...editedReason,
          };
        }),
        expiresOn: dayjs(expiryDate).endOf("day").toDate(),
        isLocked,
        delivery: {
          ...quote?.delivery,
          freightChargeExcGst: freightChargePrice,
        },
        note,
      };

      const result = await apiClient.post<SearchQuotesResponse, OfferQuotePayload>(
        `/quotes/${quote.id}/offer`,
        payload
      );
      if (result && result.status === 200 && result.data) {
        notify("Quote sent");
        navigate("/quotes");
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const pickupStore = useSelector(selectStoreById(quote?.delivery.pickupStoreId ?? undefined));

  const renderEditPickUpStoreButton = React.useMemo(() => {
    const onClick = () => {
      // TODO:Add behaviour if TFO can edit the delivery
    };

    if (pickupStore) {
      const { orgUnitName, address } = pickupStore;
      return (
        <PadActionButton
          onClick={onClick}
          buttonTestId={TestIds.feat.quotes.sendOffer.location}
          title="Pick up from:"
          subtitle={
            <Typography variant="body2" component="span">
              <Typography variant="body2" component="p" fontWeight={700}>
                {orgUnitName}
              </Typography>
              {address}
            </Typography>
          }
          isEditEnabled={false}
        />
      );
    }
    if (quote?.delivery.deliveryAddress) {
      const { streetAddress, name, city, postcode } = quote.delivery.deliveryAddress;
      return (
        <PadActionButton
          onClick={onClick}
          buttonTestId={TestIds.components.address.editButton}
          title="Deliver to:"
          subtitle={
            <Typography variant="body2" component="span">
              <Typography variant="body2" component="p" fontWeight={700}>
                {name}
              </Typography>
              {streetAddress}
              <br />
              {`${city}, ${postcode}`}
            </Typography>
          }
          isEditEnabled={false}
        />
      );
    }
  }, [pickupStore]);

  // This will store the emails keyed by accountNumber
  const [emailMap, setEmailMap] = useState<
    Map<string, { email: string; error: string | undefined }>
  >(new Map());

  useEffect(() => {
    // Sync the email map with the accountList
    const updatedEmailMap = new Map(emailMap); // Clone the current email map

    accountList.forEach((account) => {
      if (!updatedEmailMap.has(account.accountNumber)) {
        // Add account to the email map if it doesn't exist
        updatedEmailMap.set(account.accountNumber, {
          email: account.accountEmail ?? "",
          error: isInvalidEmail(account.accountEmail ?? ""),
        });
      }
    });

    // Remove any emails from the map that no longer exist in the accountList
    Array.from(updatedEmailMap.keys()).forEach((accountNumber) => {
      if (!accountList.find((account) => account.accountNumber === accountNumber)) {
        updatedEmailMap.delete(accountNumber);
      }
    });

    setEmailMap(updatedEmailMap);
  }, [accountList]); // Runs when accountList changes

  // Function to handle email input change
  const handleEmailChange = (accountNumber: string, newEmail: string) => {
    setEmailMap((prevEmailMap) => {
      const updatedEmailMap = new Map(prevEmailMap);
      updatedEmailMap.set(accountNumber, { email: newEmail, error: isInvalidEmail(newEmail) }); // Update email for the given account
      return updatedEmailMap;
    });
  };

  const isDefaultEmailDisabled = !!quote?.contact.email;

  const OfferAccountsComponent = React.useMemo(() => {
    return (
      <Stack spacing={2} sx={{ overflowX: "hidden", minHeight: "400px" }}>
        <MultiAccountSelector
          showEmail
          defaultAccount={account}
          accountList={accountList}
          setAccountList={setAccountList}
          farmerAccounts={farmerAccounts}
        />

        {accountList.map((acc) => (
          <Input
            label={acc.accountName}
            testId={TestIds.feat.quotes.sendOffer.emailInput}
            data-id={TestIds.feat.quotes.sendOffer.emailDataId(acc.accountNumber)}
            disabled={isDefaultEmailDisabled && acc.accountNumber === account.accountNumber}
            helperText={
              isDefaultEmailDisabled && acc.accountNumber === account.accountNumber
                ? "Disabled: You can not change the original customers email"
                : undefined
            }
            helperTextColor={colors.grey600}
            type="email"
            key={acc.accountNumber}
            disableError={!emailMap.get(acc.accountNumber)?.error}
            errorMessage={emailMap.get(acc.accountNumber)?.error}
            outlineInputProps={{
              type: "email",
            }}
            value={emailMap.get(acc.accountNumber)?.email ?? ""} // Get the email from the emailMap
            onChange={(e) => handleEmailChange(acc.accountNumber, e.target.value)} // Update email map
          />
        ))}
      </Stack>
    );
  }, [multiple, accountList, farmerAccounts, emailMap, account, quote]);

  const OfferDetailsComponent = React.useMemo(() => {
    return (
      <Stack spacing={2} sx={{ overflowX: "hidden", minHeight: "400px" }}>
        <ExpiresOnDatePicker
          key="ExpiresOnDatePicker"
          dateError={dateError}
          expiresOn={expiryDate}
          setDateError={setDateError}
          setExpiresOn={setExpiryDate}
          maxFutureDays={quoteMaxFutureDays ?? 7}
        />

        <RestrictedCheckbox
          key="RestrictedCheckbox"
          restricted={isLocked}
          setRestricted={setIsLocked}
        />
        <FreightChargeInput
          key="FreightChargeInput"
          freightCharge={freightCharge}
          setFreightCharge={setFreightCharge}
          setFreightChargePrice={setFreightChargePrice}
        />
        {renderEditPickUpStoreButton}
        <Box sx={{ minHeight: 100 }}>
          <TextField
            aria-label="Notes"
            label="Notes"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            inputProps={{ maxLength: 200 }}
            maxRows={10}
            multiline
            minRows={3}
            variant="outlined"
            helperText="Notes will be included in the quote sent to the customer."
            testId={TestIds.feat.quotes.sendOffer.note}
          />
        </Box>
      </Stack>
    );
  }, [
    dateError,
    expiryDate,
    isLocked,
    freightCharge,
    renderEditPickUpStoreButton,
    note,
    quoteMaxFutureDays,
  ]);

  const [pageIndex, setPageIndex] = React.useState(0);
  const [previousIndex, setPreviousIndex] = React.useState<number | undefined>();
  const pages = [
    {
      title: "Offer to selected accounts",
      component: OfferAccountsComponent,
      button: "Continue",
      validation: () => {
        for (const email of emailMap.values()) {
          if (isInvalidEmail(email.email)) {
            return false; // Return false if any email is invalid
          }
        }
        return true;
      },
    },
    {
      title: "Confirm offer details",
      component: OfferDetailsComponent,
      button: "Send Offer",
      validation: () => true,
    },
  ];

  const currentAction = React.useMemo(() => {
    if (loading) {
      return <Loader title="Sending order" type="inline-flex" sx={{ ml: "auto" }} />;
    }
    return (
      <Button
        sx={{ alignSelf: "flex-end", marginLeft: "auto", width: isSm ? "100%" : "auto" }}
        variant="contained"
        data-testid={TestIds.feat.drawdown.continueButton}
        onClick={() => {
          if (pages[pageIndex].validation()) {
            if (pageIndex === pages.length - 1) {
              onSubmit();
            } else {
              setPageIndex((pageIndex + 1) % pages.length);
            }
          }
        }}
      >
        {pages[pageIndex].button}
      </Button>
    );
  }, [pages, pageIndex, loading]);

  return (
    <Modal
      title="Send Quote"
      open={isModalOpen}
      maxWidth="sm"
      handleClose={() => setIsModalOpen(false)}
      header={
        <Steppers
          maxSteps={pages.length}
          activeStep={pageIndex}
          title={pages[pageIndex].title}
          previousStep={previousIndex}
          setPageIndex={(value) => {
            if (value < pageIndex || (value > pageIndex && pages[pageIndex].validation())) {
              setPreviousIndex(pageIndex);
              setPageIndex(value);
            }
          }}
        />
      }
      footer={currentAction}
      paperProps={{
        sx: {
          height: isSm ? "inherit" : "auto",
          minHeight: "650px",
        },
      }}
      data-testid={TestIds.feat.quotes.sendOffer.modal}
    >
      <Box>{pages[pageIndex].component}</Box>
      {error && (
        <Typography variant="body2" color="error">
          Something went wrong, please try again.
        </Typography>
      )}
    </Modal>
  );
};
